<template>
  <section
    :class="{
      'ui-copy-input--mini': isMini,
    }"
  >
    <v-tooltip
      v-model="copied"
      location="bottom"
      :offset="[-25, 0]"
      style="left: 50%; margin-top: -226px"
      :open-on-hover="false"
      :open-on-focus="false"
      :open-on-click="false"
    >
      <template #activator="{ props: tooltipProps }">
        <div v-bind="tooltipProps" class="ui-copy-input__tooltip-anchor">
          <slot name="label">
            <ui-label
              v-if="label"
              :name="name"
              :plan-limits="planLimits"
              :text="label"
              :help-tooltip="labelHelp"
            />
          </slot>

          <div class="ui-copy-input__control" @click="copy">
            <v-text-field
              ref="inputRef"
              :model-value="preparedValue"
              readonly
              variant="underlined"
              density="compact"
              hide-details
              :class="{
                'ui-copy-input': true,
                'copy-input': true,
                notValue: !hasValue,
              }"
            >
              <template #append-inner>
                <ui-icon
                  :color="colors.green"
                  class="copy-icon"
                  size="12"
                  icon="fas fa-copy"
                />
                <span v-if="!isMini" class="ui-copy-input-button">{{ t('Copy') }}</span>
                <ui-plan-limits v-if="showPlanLimitsIcon" class="plan-limits-icon" />
              </template>
            </v-text-field>
          </div>
        </div>
      </template>
      <span>{{ t('Copied!') }}</span>
    </v-tooltip>
  </section>
</template>

<script setup lang="ts">
import { computed, ref } from 'vue';
import { useColors, useI18n, usePlanLimits, type PlanLimitsProp } from '@/composables';

const props = withDefaults(
  defineProps<{
    placeholder?: string;
    isMini?: boolean;
    value?: string;
    modelValue?: string;
    label?: string;
    labelHelp?: string;
    planLimits?: PlanLimitsProp;
    name?: string;
  }>(),
  {
    isMini: false,
  },
);

const { t } = useI18n();
const colors = useColors();

const { showPlanLimitsIcon, handleClickPlanLimits } = usePlanLimits(
  () => props.planLimits,
  false,
);

const copied = ref(false);
const inputRef = ref<{
  $el?: HTMLElement;
} | null>(null);

const hasValue = computed(() => {
  const raw = props.modelValue !== undefined ? props.modelValue : props.value;
  return raw != null && String(raw).length > 0;
});

const preparedValue = computed(() => {
  if (showPlanLimitsIcon.value) {
    return '*******************************';
  }
  const raw = props.modelValue !== undefined ? props.modelValue : props.value;
  if (raw === undefined || raw === null) {
    return '';
  }
  return String(raw);
});

const copy = () => {
  if (showPlanLimitsIcon.value) {
    handleClickPlanLimits();
    return;
  }
  if (!hasValue.value) {
    return;
  }
  const root = inputRef.value?.$el;
  const inputEl = root?.querySelector?.('input') as HTMLInputElement | null;
  if (!inputEl) {
    return;
  }
  inputEl.select();
  document.execCommand('copy');
  copied.value = true;
  setTimeout(() => {
    copied.value = false;
  }, 1500);
};
</script>

<style lang="scss" scoped>
section :deep(.v-input.copy-input) {
  padding-bottom: 10px !important;
}

.ui-copy-input__tooltip-anchor {
  width: 100%;

  .ui-copy-input__control {
    width: 100%;
  }
}

.plan-limits-icon {
  margin-top: -10px;
  margin-left: 4px;
}

.ui-copy-input {
  :deep(.v-input__control) {
    border-bottom: none !important;
  }

  :deep(.v-field) {
    border-bottom: 1px solid var(--color-gray10) !important;
  }

  :deep(.v-field__field) {
    border-bottom: none !important;
  }

  :deep(.v-input__append),
  :deep(.v-field__append-inner) {
    border-bottom: none !important;
    display: flex;
    flex-shrink: 0;
    align-items: center;
    flex-wrap: nowrap;
    white-space: nowrap;
    padding: 0 4px 0 16px;
    margin-inline-start: 0 !important;
    cursor: pointer;
  }

  :deep(.v-field__outline) {
    display: none;
  }

  :deep(.v-field__loader) {
    display: none;
  }

  :deep(.v-field__overlay) {
    display: none;
  }

  :deep(.v-input__details) {
    display: none;
  }

  :deep(.v-field__input) {
    font-size: 13px;
    color: var(--color-gray40);
    padding: 2px 0 !important;
    letter-spacing: initial;
    line-height: 20px;
    min-height: 24px;
    cursor: pointer;
    text-overflow: ellipsis;
  }

  :deep(input) {
    border: none !important;
    padding: 2px 0 !important;
    height: auto !important;
    line-height: 20px;
    min-height: 0 !important;
    border-radius: 0 !important;
    background: transparent !important;
    box-shadow: none !important;
    color: var(--color-gray40);
  }

  :deep(.v-input__append-inner) {
    align-self: flex-end;
    padding-bottom: 6px !important;
    padding-right: 4px !important;
  }
}

.ui-copy-input-button {
  text-transform: capitalize;
  margin-left: 6px;
  font-size: 13px;
  font-weight: normal;
  color: var(--color-green);
  white-space: nowrap;
}
</style>

<style lang="scss" scoped>
.ui-copy-input--mini {
  :deep(.v-input__append-inner) {
    padding: 0 4px;
  }

  :deep(.copy-input .v-input__append-inner) {
    padding-bottom: 2px !important;
  }
}

:deep(.copy-input) {
  .v-input__append-inner {
    align-self: flex-end;
    padding-bottom: 6px !important;
    padding-right: 4px !important;
  }
}

.plan-limits-icon {
  margin-top: -10px;
  margin-left: 4px;
}
</style>
